import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import { acceptDocument, getCurrentVersions, formatVersion } from '../utils/legalApi';
import type { CurrentVersionsResponse } from '../utils/legalApi';
import { useAuth } from '../hooks/useAuth';

const DOCUMENT_ORDER = ['tos', 'privacy'] as const;

const DOCUMENT_LABELS: Record<string, string> = {
  tos: 'Terms of Service',
  privacy: 'Privacy Policy',
};

interface LegalAcceptanceModalProps {
  needsAcceptance: string[];
  currentVersions: Record<string, string>;
  onComplete: () => void;
}

export const LegalAcceptanceModal: React.FC<LegalAcceptanceModalProps> = ({
  needsAcceptance,
  currentVersions,
  onComplete,
}) => {
  const { signOut } = useAuth();
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [versionsData, setVersionsData] = useState<CurrentVersionsResponse | null>(null);

  // Fixed order: TOS first, then Privacy
  const orderedDocs = DOCUMENT_ORDER.filter(d => needsAcceptance.includes(d));

  // Fetch presigned URLs for "View" links
  useEffect(() => {
    getCurrentVersions()
      .then(data => setVersionsData(data))
      .catch(() => {
        // fail silently — links will not appear if presigned URLs unavailable
      });
  }, []);

  const allChecked =
    orderedDocs.length > 0 && orderedDocs.every(doc => checked[doc] === true);

  const handleCheckbox = (doc: string) => {
    setChecked(prev => ({ ...prev, [doc]: !prev[doc] }));
  };

  const handleAccept = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      // Accept documents sequentially so partial failures leave only
      // un-accepted documents to retry on the next attempt.
      for (const doc of orderedDocs) {
        await acceptDocument({
          document_type: doc,
          version: currentVersions[doc],
        });
      }
      onComplete();
    } catch {
      setError('Failed to record acceptance. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    signOut();
  };

  // Explanation copy changes based on how many docs need acceptance
  const docList = orderedDocs.map(d => DOCUMENT_LABELS[d] ?? d).join(' and ');
  const explanationText = `We've updated our ${docList}. Please review and accept the new version${orderedDocs.length > 1 ? 's' : ''} to continue.`;

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        // Prevent dismissal — user must Accept or Decline
        if (reason === 'backdropClick') return;
      }}
    >
      <DialogTitle>Updated Documents</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {explanationText}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {orderedDocs.map(doc => {
            const presignedUrl =
              versionsData?.[doc as keyof CurrentVersionsResponse]?.presignedUrl;
            const version = currentVersions[doc];
            const label = DOCUMENT_LABELS[doc] ?? doc;

            return (
              <Box
                key={doc}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked[doc] ?? false}
                      onChange={() => handleCheckbox(doc)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {label}
                      </Typography>
                      {version && (
                        <Typography variant="caption" color="text.secondary">
                          {formatVersion(version)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                {presignedUrl && (
                  <Link
                    href={presignedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ ml: 1, flexShrink: 0 }}
                  >
                    View
                  </Link>
                )}
              </Box>
            );
          })}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={handleDecline} disabled={isSubmitting}>
          Decline and Log Out
        </Button>
        <Button
          variant="contained"
          disabled={!allChecked || isSubmitting}
          onClick={handleAccept}
        >
          {isSubmitting ? <CircularProgress size={20} /> : 'Accept'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
